import { MainHeader, Section } from '@/components/shared/Content';
import JoinCTA from '@/components/shared/JoinCTA';
import { useShowHeaderCtaOnScroll } from '@/hooks/useShowHeaderCtaOnScroll';
import { PRODUCT_PET } from '@/utils/products';
import { Flex, fadeInAnimation, font } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';

import type React from 'react';
import styled from 'styled-components';
import { PartnerLogo } from '../shared/PartnerLogo';
import { DiscountBanner } from './DiscountBanner';
import PetAnimation from './PetAnimation';
import ScrollArrow from './ScrollArrow';
import { TruspilotSection } from './TruspilotSection';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

const TopSectionContainer = styled(Section)`
  position: relative;
  background-color: transparent;
  z-index: 2;
  ${MEDIA_QUERIES.tabletAndDesktop`
    height: calc(100vh - 100px);
  `}

  @media (max-height: 1024px) {
    min-height: 730px;
  }
`;

const TruspilotWrapper = styled(Flex)`
  justify-content: center;
  pointer-events: default;
  width: 100%;
  position: absolute;
  top: 300px;
  bottom: auto;
  z-index: 1;
  ${MEDIA_QUERIES.tablet`
    top: auto;
    bottom: 50px;
  `}
  ${MEDIA_QUERIES.desktop`
    top: auto;
    bottom: 120px;
  `}
`;

const StyledTitle = styled(MainHeader)`
  margin: 0 29px;
  ${MEDIA_QUERIES.tabletAndDesktop`
    white-space: pre;
  `}
`;

const Subtitle = styled.h2`
  text-align: center;
  margin: 20px 39px 0px;
  ${font('main', {
    lineHeight: '2.8rem',
    fontSize: '2.0rem',
    fontWeight: 'normal',
  })};
`;

const SubtitleInner = styled.span`
  animation: ${fadeInAnimation} 0.3s ease-in;
`;

export const MainCTA = styled(JoinCTA)`
  margin-top: 56px;
  width: 233px;

  ${MEDIA_QUERIES.mobile`
      margin-top: 28px;
    `}
`;

const ContentContainer = styled(Flex)`
  position: relative;
  top: -20%;
`;

const ScrollArrowContainer = styled.div`
  width: 30px;
  position: relative;

  ${MEDIA_QUERIES.tabletAndDesktop`
    width: 42px;
  `}

  @media (max-height: 255px) {
    display: none;
  }
`;

const StyledScrollArrow = styled(ScrollArrow)`
  position: absolute;
  top: 60px;
  left: auto;
  right: auto;

  ${MEDIA_QUERIES.tabletAndDesktop`
    top: 120px;
  `}
`;

const PetAnimationContainer = styled(Flex)<{ showTruspilotWidget: boolean }>`
  ${font('alt', { fontSize: '2.0rem' })}
  height: calc(330px + 150 * (667px - 100vh) / -145); /* lerp in css! */
  min-height: 400px;
  pointer-events: none;
  position: relative;
  width: 150%;
  margin-bottom: 20px;

  ${MEDIA_QUERIES.tabletAndDesktop`
    height: 100vh;
    position: absolute;
    bottom: 150px;
  `};

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    left: -25%;
    right: 0;
    width: 150%;
  }

  @media (max-width: 400px) {
    margin-top: ${props => (props.showTruspilotWidget ? '120px' : '0')};
  }
`;

const StyledPartnerLogo = styled(PartnerLogo)`
  margin-top: 0px;
  margin-bottom: 0;
  ${MEDIA_QUERIES.desktop`
    margin-bottom: 40px;
  `}
`;

export interface HeroProps {
  title: string;
  subtitle: string;
  showTruspilotWidget?: boolean;
  showArrow?: boolean;
  partnerLogo?: string;
  ctaLabel?: string;
  ctaUrl?: string;
}

export const Hero: React.FC<HeroProps> = ({
  title,
  subtitle,
  showTruspilotWidget,
  showArrow = true,
  partnerLogo,
  ctaLabel = 'Check our prices',
  ctaUrl,
}) => {
  const { ref } = useShowHeaderCtaOnScroll();
  const router = useRouter();

  const analyticsParams = useMemo(() => {
    return {
      pet_lp_type: 'default',
      variant: router.query.dynamic_location != null ? 'dynamic_lp' : 'default',
    };
  }, [router.query.dynamic_location]);

  return (
    <TopSectionContainer justifyContent="center" mt={[0, 40]}>
      <ContentContainer flexDirection="column" alignItems="center">
        <StyledPartnerLogo src={partnerLogo} />
        <StyledTitle tabIndex={0}>{title.trimEnd()}</StyledTitle>
        <Subtitle>
          <SubtitleInner tabIndex={0}>{subtitle.trimEnd()}</SubtitleInner>
        </Subtitle>
        <MainCTA
          size="big"
          flavor="primary"
          id="gtm_button_pet_check_prices"
          glowing
          ref={ref}
          data-source="pet"
          product={PRODUCT_PET}
          data-position="top"
          customUrl={ctaUrl}
          analyticsParams={analyticsParams}
        >
          {ctaLabel}
        </MainCTA>
        <DiscountBanner />
        {showArrow && (
          <ScrollArrowContainer>
            <StyledScrollArrow />
          </ScrollArrowContainer>
        )}
      </ContentContainer>
      {showTruspilotWidget != null && (
        <TruspilotWrapper>
          <TruspilotSection>
            <div
              className="trustpilot-widget"
              data-locale="en-US"
              data-template-id="5419b732fbfb950b10de65e5"
              data-businessunit-id="58c6e2b50000ff00059e674d"
              data-style-height="24px"
              data-style-width="100%"
              data-theme="light"
              data-font-family="Lato"
            >
              <a href="https://www.trustpilot.com/review/lemonade.com" target="_blank" rel="noopener noreferrer">
                Trustpilot
              </a>
            </div>
          </TruspilotSection>
        </TruspilotWrapper>
      )}
      <PetAnimationContainer
        showTruspilotWidget={showTruspilotWidget}
        flexDirection="row"
        justifyContent={['space-around', 'center']}
      >
        <PetAnimation />
      </PetAnimationContainer>
    </TopSectionContainer>
  );
};
